import styles from './certificate.module.css';
import campaignStyles from '../campaigns/campaign.module.css';
// campaign-certificate.tsx — the Certificate tab panel + the overview sidebar
// card. When earned the panel renders the *actual* generated PDF in an <iframe>
// (so the preview is always identical to the download), plus the validation
// code/link. Otherwise a locked card with the ≥80% requirement and progress.
import { Label } from '../../../components/label';
import { ProgressBar } from '../../../components/progress-bar';
import { Card } from '../../../components/card';
import { Button } from '../../../components/button';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCampaignCertificate, useCertificatePdf } from '../../../api/student/certificates/hooks';

/**
 * Renders page 1 of the certificate PDF into a <canvas> (via PDF.js) so the
 * preview looks like the certificate itself — no PDF-viewer chrome / iframe
 * borders. Falls back to an <iframe> if PDF.js fails to load.
 */
function CertificatePdfCanvas({ blob, fallbackUrl, title }: { blob: Blob; fallbackUrl: string; title: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pdfjs = await import('pdfjs-dist');
        const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
        pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
        const data = await blob.arrayBuffer();
        const pdf = await pdfjs.getDocument({ data }).promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 2 });
        const canvas = canvasRef.current;
        if (cancelled || !canvas) return;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        // pdfjs 6 takes the canvas itself; `canvasContext` alone is the
        // backwards-compatible path and no longer satisfies the type.
        await page.render({ canvas, canvasContext: ctx, viewport }).promise;
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [blob]);

  if (failed) {
    return <iframe title={title} className={`${styles.certPdf}`} src={`${fallbackUrl}#toolbar=0&navpanes=0&view=FitH`} />;
  }
  return <canvas ref={canvasRef} className={`${styles.certCanvas}`} />;
}

/** Certificate tab content. */
export function CampaignCertificatePanel({ campaignId }: { campaignId: string }) {
  const { t } = useTranslation();
  const { data, isLoading } = useCampaignCertificate(campaignId);
  const pdf = useCertificatePdf(campaignId, !!data?.earned);

  if (isLoading || !data) {
    return <Card framed padded={false} className={campaignStyles.cdPlaceholder}><p>{t('common.loading')}</p></Card>;
  }

  if (!data.earned) {
    return (
      <Card framed padded={false} className={`${styles.certLocked}`}>
        <Label>{t('student.campaign.certificate.title')}</Label>
        <h3 className={`${styles.certLocked__title}`}>{t('student.campaign.certificate.lockedTitle')}</h3>
        <p className={`${styles.certLocked__desc}`}>{t('student.campaign.certificate.lockedDesc', { required: data.requiredPct })}</p>
        <ProgressBar value={data.pct} grow />
        <p className={`${styles.certLocked__pct}`}>
          {t('student.campaign.certificate.progress', { completed: data.completed, total: data.total, pct: data.pct })}
        </p>
      </Card>
    );
  }

  const validateHref = data.code
    ? `${window.location.origin}/?validate=${encodeURIComponent(data.code)}`
    : undefined;

  return (
    <div className={`${styles.certPanel}`}>
      <div className={`${styles.certPdfWrap}`}>
        {pdf.loading && !pdf.blob && <div className={`${styles.certPdfState}`}>{t('common.loading')}</div>}
        {pdf.error && <div className={`${styles.certPdfState}`}>{t('student.campaign.certificate.previewError')}</div>}
        {pdf.blob && pdf.url && (
          <CertificatePdfCanvas
            blob={pdf.blob}
            fallbackUrl={pdf.url}
            title={t('student.campaign.certificate.title')}
          />
        )}
      </div>

      <div className={`${styles.certPanel__actions}`}>
        {pdf.url ? (
          <Button as="a" href={pdf.url} download={pdf.filename}>
            ↓ {t('student.campaign.certificate.download')}
          </Button>
        ) : (
          <Button disabled>{t('common.loading')}</Button>
        )}
        {validateHref && (
          <a className={`${styles.certPanel__validateLink}`} href={validateHref} target="_blank" rel="noopener noreferrer">
            {t('student.campaign.certificate.openValidation')} →
          </a>
        )}
      </div>

      {data.code && (
        <p className={`${styles.certPanel__code}`}>
          {t('student.campaign.certificate.code')}: <code>{data.code}</code>
        </p>
      )}
      <p className={`${styles.certPanel__hint}`}>{t('student.campaign.certificate.validationHint')}</p>
    </div>
  );
}